import { Component, DestroyRef, ElementRef, inject, signal, computed, effect, OnInit, AfterViewChecked, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpperCasePipe } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from 'primeng/tooltip';
import {
  faBook,
  faBullhorn,
  faFileCertificate,
  faChartLine,
  faCheckCircle,
  faChevronLeft,
  faChevronRight,
  faClockRotateLeft,
  faCog,
  faCube,
  faFolderTree,
  faHouse,
  faLayerGroup,
  faLightbulb,
  faPhotoVideo,
  faShieldAlt,
  faThumbsUp,
  faUserFriends,
  faUsers,
  faGlobe,
} from '@fortawesome/pro-duotone-svg-icons';
import { filter } from 'rxjs/operators';

import { AuthService } from '../../services/auth.service';
import { SystemAccessService } from '../../services/system-access.service';
import { TemporalContextService } from '../../services/temporal-context.service';
import { MembershipService } from '../../services/membership.service';
import { SystemPermissionName } from '../../models/system-access.model';
import { TranslateModule } from '@ngx-translate/core';
import { TourTargetDirective } from '../../directives/tour-target.directive';
import { formatCount } from '../../utils/count.utils';

/** Routes that live at asOf in "Time travel" mode — everything else dims. */
const TEMPORAL_CONTENT_SPINE = ['/manual', '/media', '/drive'];

interface NavItem {
  label: string;
  labelKey?: string; // Translation key for the label
  icon: any;
  route: string;
  badge?: number;
  /**
   * Single permission OR an array; array semantic is OR — item is visible to any holder.
   * Admin/Owner bypass via SystemAccessService.admin() (they see every nav item).
   */
  permission?: SystemPermissionName | readonly SystemPermissionName[];
  /**
   * False = one of the public shell's items, shown to a caller in PUBLIC MODE (a public brand's visitor:
   * anonymous, or signed in with no membership). A member meets the permission gate like every other
   * item — the flag opens nothing for them. Default (true): only shown to authenticated callers.
   */
  requiresAuth?: boolean;
  /**
   * The address the item links to in the public shell, when it differs from a member's: Home is the brand
   * portal at `/` for a visitor and the dashboard at `/home` for a member.
   */
  publicRoute?: string;
  separatorBefore?: boolean; // Renders a subtle divider above this item
  /**
   * Admin-privilege-only item (no permission member expresses "Admin+ only" and none should be
   * faked): visible to Admin/Owner and to platform-root monitors (read parity), never to
   * permission-holding ordinary members.
   */
  adminOnly?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [UpperCasePipe, RouterModule, FontAwesomeModule, TooltipModule, TranslateModule, TourTargetDirective],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit, AfterViewChecked {
  protected readonly formatCount = formatCount;

  private authService = inject(AuthService);
  private systemAccessService = inject(SystemAccessService);
  private router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly temporal = inject(TemporalContextService);
  private readonly membership = inject(MembershipService);

  // Derive directly from auth service — demo-aware so the demo identity drives sidebar visibility
  isAuthenticated = computed(() => this.authService.signedIn());

  // Derive admin from SystemAccessService reactive signal
  isAdmin = computed(() => this.systemAccessService.admin());

  constructor() {
    // Bootstrap privilege level fetch when user is signed in (real auth OR demo).
    // The signal updates reactively — all consumers (sidebar, dashboard, tiles) see it.
    effect(() => {
      if (this.authService.signedIn()) {
        this.systemAccessService.getPrivilegeLevel();
      }
    });
  }

  // Font Awesome icons
  faHouse = faHouse;
  faBook = faBook;
  faPhotoVideo = faPhotoVideo;
  faFolderTree = faFolderTree;
  faLayerGroup = faLayerGroup;
  faLightbulb = faLightbulb;
  faUsers = faUsers;
  faUserFriends = faUserFriends;
  faChartLine = faChartLine;
  faCog = faCog;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faCube = faCube;
  faBullhorn = faBullhorn;
  faCheckCircle = faCheckCircle;
  faThumbsUp = faThumbsUp;
  faShieldAlt = faShieldAlt;

  // State
  isCollapsed = signal(false);
  activeRoute = signal('');

  // Sliding indicator position
  indicatorTop = signal<number | null>(null);
  private needsIndicatorUpdate = false;

  // Navigation items
  // requiresAuth: false = the public shell's three items (Home · Manual · Media), shown to a caller in public mode
  // requiresAuth: true (default) = only visible to authenticated users
  // Overview (Home + Brand Hub) → Content pillars → Workflow pillars → Intelligence pillars
  mainNavItems: NavItem[] = [
    // Overview — Home (yours) + Brand Hub (the brand's): the two landing surfaces. For a public brand's
    // visitor Home is the brand portal at `/`, inside the shell.
    { label: 'Home', labelKey: 'navigation.home', icon: faHouse, route: '/home', requiresAuth: false, publicRoute: '/' },
    { label: 'Brand Hub', labelKey: 'navigation.hub', icon: faCube, route: '/hub', permission: 'VIEW_HUB' },
    // Content pillars — the two public modules
    { label: 'Manual', labelKey: 'navigation.manual', icon: faBook, route: '/manual', separatorBefore: true, permission: 'ACCESS_MANUAL', requiresAuth: false },
    { label: 'Media', labelKey: 'navigation.media', icon: faPhotoVideo, route: '/media', permission: 'ACCESS_MEDIA', requiresAuth: false },
    { label: 'Drive', labelKey: 'navigation.drive', icon: faFolderTree, route: '/drive', permission: 'ACCESS_DRIVE' },
    { label: 'Collections', labelKey: 'navigation.collections', icon: faLayerGroup, route: '/collections', permission: 'ACCESS_COLLECTIONS' },
    // Workflow
    { label: 'Selects', labelKey: 'navigation.selects', icon: faThumbsUp, route: '/selects', separatorBefore: true, permission: 'VIEW_SELECTS' },
    { label: 'Reviews', labelKey: 'navigation.reviews', icon: faCheckCircle, route: '/reviews', permission: 'VIEW_REVIEWS' },
    { label: 'Campaigns', labelKey: 'navigation.campaigns', icon: faBullhorn, route: '/campaigns', permission: 'VIEW_CAMPAIGNS' },
    // Intelligence
    { label: 'Analytics', labelKey: 'navigation.analytics', icon: faChartLine, route: '/analytics', separatorBefore: true, permission: 'VIEW_ANALYTICS' },
    { label: 'Insights', labelKey: 'navigation.insights', icon: faLightbulb, route: '/insights', permission: 'VIEW_INSIGHTS' },
    { label: 'Compliance', labelKey: 'navigation.compliance', icon: faShieldAlt, route: '/compliance', permission: 'VIEW_COMPLIANCE' },
  ];

  /**
   * Main nav filtered to what the member's permissions open. The brand's plan hides nothing here:
   * every plan carries every module.
   */
  readonly visibleMainNavItems = computed(() =>
    this.mainNavItems.filter(item => this.isNavItemVisible(item)));

  // Each admin nav item gated by its specific permission(s).
  // Admin/Owner pass via isNavItemVisible's admin() bypass — they see every item.
  // Order: frequency descending (the people pair first), the governance tail
  // (Activity log = the record) beside the bottom-anchored Settings.
  adminNavItems: NavItem[] = [
    { label: 'Members', labelKey: 'navigation.admin.members', icon: faUsers, route: '/admin/members',
      permission: ['MANAGE_MEMBERS', 'APPROVE_MEMBERS'] },
    { label: 'Groups', labelKey: 'navigation.admin.groups', icon: faUserFriends, route: '/admin/groups',
      permission: ['MANAGE_CONTENT_GROUPS', 'MANAGE_SYSTEM_GROUPS'] },
    { label: 'Brand portal', labelKey: 'navigation.admin.portal', icon: faGlobe, route: '/admin/portal',
      permission: 'MANAGE_PORTAL_COLLECTIONS' },
    { label: 'Licenses', labelKey: 'navigation.admin.licenses', icon: faFileCertificate, route: '/admin/licenses',
      permission: 'MANAGE_LICENSES' },
    { label: 'Activity log', labelKey: 'navigation.admin.activityLog', icon: faClockRotateLeft, route: '/admin/activity',
      adminOnly: true },
    { label: 'Settings', labelKey: 'navigation.admin.settings', icon: faCog, route: '/admin/settings',
      permission: ['MANAGE_BRAND_SETTINGS', 'MANAGE_LOCALES', 'MANAGE_MEMBERS', 'MANAGE_SYSTEM_ROLES',
                   'MANAGE_CONTENT_ROLES', 'MANAGE_CONTENT_GROUPS', 'MANAGE_SYSTEM_GROUPS',
                   'MANAGE_RESOURCE_CATEGORIES', 'MANAGE_ANNOUNCEMENTS', 'MANAGE_BILLING', 'EXPORT_DATA'] },
  ];

  // True if the user is Admin/Owner OR holds at least one permission that grants
  // access to any admin nav item — used to gate the "Admin" sidebar section header.
  // Platform-root monitors see the section too (read-only pages): their permission set
  // carries only read-grant keys, never the Manage* keys these items require, so without
  // the explicit root leg the header would stay hidden for exactly its parity audience.
  showAdminSection = computed(() => {
    if (this.isAdmin() || this.systemAccessService.isRoot()) return true;
    if (!this.isAuthenticated()) return false;
    const held = this.systemAccessService.permissions();
    return this.adminNavItems.some(item => {
      if (!item.permission) return false;
      const required = Array.isArray(item.permission) ? item.permission : [item.permission as SystemPermissionName];
      return required.some(p => held.has(p));
    });
  });

  ngOnInit() {
    // Track active route
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.activeRoute.set(event.urlAfterRedirects);
        this.needsIndicatorUpdate = true;
      });

    // Set initial active route
    this.activeRoute.set(this.router.url);
    this.needsIndicatorUpdate = true;
  }

  ngAfterViewChecked() {
    if (this.needsIndicatorUpdate) {
      this.needsIndicatorUpdate = false;
      this.updateIndicatorPosition();
    }
  }

  private updateIndicatorPosition() {
    const el: HTMLElement = this.elementRef.nativeElement;
    const activeLink = el.querySelector('.sidebar-link.active') as HTMLElement;
    const navContainer = el.querySelector('.sidebar-nav-area') as HTMLElement;
    if (activeLink && navContainer) {
      const linkRect = activeLink.getBoundingClientRect();
      const containerRect = navContainer.getBoundingClientRect();
      this.indicatorTop.set(linkRect.top - containerRect.top + (linkRect.height - 20) / 2);
    } else {
      this.indicatorTop.set(null);
    }
  }

  toggleSidebar() {
    this.isCollapsed.set(!this.isCollapsed());
  }

  isActive(route: string): boolean {
    const currentRoute = this.activeRoute();
    return currentRoute === route || currentRoute.startsWith(route + '/');
  }

  /** Maps a nav-item route to its tour-target key. Empty string = not a tour anchor. */
  private static readonly TOUR_KEYS_BY_ROUTE: Record<string, string> = {
    '/manual': 'manual-nav',
    '/media': 'media-nav',
    '/drive': 'drive-nav',
    '/selects': 'selects-nav',
    '/reviews': 'reviews-nav',
    '/campaigns': 'campaigns-nav',
  };

  tourKeyForRoute(route: string): string {
    return SidebarComponent.TOUR_KEYS_BY_ROUTE[route] ?? '';
  }

  /** The item's address for the reader on screen: its public address in public mode, the member's otherwise. */
  linkFor(item: NavItem): string {
    return this.membership.publicMode() && item.publicRoute ? item.publicRoute : item.route;
  }

  /**
   * True for items OUTSIDE the past view while "Time travel" is active.
   * Dimmed + described, never disabled: the item stays enabled and focusable (a
   * clickable control must not announce disabled), and clicking it lands on the boundary
   * interstitial via temporalGuard — the route-level dual of this treatment.
   */
  isTemporalExcluded(route: string): boolean {
    return this.temporal.active()
      && !TEMPORAL_CONTENT_SPINE.some(p => route === p || route.startsWith(p + '/'));
  }

  isNavItemVisible(item: NavItem): boolean {
    // The public shell's items: a public brand's visitor (anonymous, or signed in with no membership) sees
    // Home, Manual and Media. In public mode ALONE — a member meets the permission gate below like everyone.
    if (item.requiresAuth === false && this.membership.publicMode()) {
      return true;
    }
    if (!this.isAuthenticated()) {
      return false;
    }
    // Admin-privilege-only items: Admin/Owner plus platform-root monitors (read parity) —
    // permissions never open these.
    if (item.adminOnly) {
      return this.isAdmin() || this.systemAccessService.isRoot();
    }
    // No permission specified — visible to all authenticated members
    if (!item.permission) {
      return true;
    }
    // Admin/Owner bypass — they hold every permission member from the server. Platform-root
    // monitors compose the same leg: BOTH gates must open (section header alone would render
    // an empty admin header, item gate alone would never show).
    if (this.isAdmin() || this.systemAccessService.isRoot()) {
      return true;
    }
    // OR semantic for permission arrays
    const held = this.systemAccessService.permissions();
    const required = Array.isArray(item.permission) ? item.permission : [item.permission as SystemPermissionName];
    return required.some(p => held.has(p));
  }

  navigate(route: string) {
    this.router.navigate([route]);
  }
}
